import * as React from 'react';
import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { MenuItemType, MenuItem, MenuPosition, CreateButtonOption } from '../../../typings/menu';
import { StyledLeftSideMenu, HeaderMenuItem, MenuItemsWrapper, UserInfoWrapper } from './styles';
import RoutesUtils from '../../../utils/routes';
import { MenuActionKeys } from '../../../containers/MenuManager/actions';
import { PageOverlay } from '../../PageOverlay/styles';
import { DriveItemListOrigin } from '../../../containers/DriveItemListPage/constants';
import LeftMenuItem from '../../../containers/MenuManager/Left/MenuItem';
import HeaderLogo from '../../../containers/MenuManager/Header/Logo';
import UserAvatar from '../../../containers/ProfilePage/UserAvatar';
import { AvatarSize } from '../../../containers/ProfilePage/reducer';
import UserInfo from '../../../containers/ProfilePage/UserInfo';
import ButtonCreate from '../../../containers/ButtonCreate';
import ButtonCreateNote from '../../../containers/ButtonCreateNote';
import BackToPublication from '../../../containers/BackToPublication';

export interface LeftSideMenuProps {
	filteredMenuItems: MenuItem[];
	newItemsNb: number;
	isOpen: boolean;
	hasLogOutButton: boolean;
	hasProfile: boolean;
	hasProposalStatsPage: boolean;
	hasCookieSettings: boolean;
	hasNotesPage: boolean;
	privateDirId: number | undefined;
	sharedDirId: number | undefined;
	archiveDirId: number | undefined;
	isProfileLoaded: boolean;
	hasDownloads: boolean;
	dispatch: Dispatch<StoreState>;
}

class LeftSideMenu extends React.Component<LeftSideMenuProps, {}> {

	constructor(props: LeftSideMenuProps) {
		super(props);
		this.getMenuItems = this.getMenuItems.bind(this);
	}

	renderMenuItem = (item: MenuItem, index: number) => {
		if (item) {
			return (
				<LeftMenuItem
					menuItem={item}
					key={index}
					badge={item.badge}
				/>
			);
		}

		return null;
	}

	render() {
		const { isOpen, filteredMenuItems, hasProfile } = this.props;
		const menuItems = this.getMenuItems(filteredMenuItems);

		return (
			<>
				<PageOverlay isOpen={isOpen} onClick={this.handleOverlayClick} data-gallery-slot="leftSideMenu" />
				<StyledLeftSideMenu className="StyledLeftSideMenu" isOpen={isOpen} data-gallery-slot="leftSideMenu">
					<MenuItemsWrapper className="MenuItemsWrapper">
						<HeaderMenuItem className="HeaderMenuItem" hasProfile={hasProfile}>
							{hasProfile
								? (
									<UserInfoWrapper onClick={this.handleAvatarClick}>
										<UserAvatar size={AvatarSize.SMALL} />
										<UserInfo isSmall={true} />
									</UserInfoWrapper>
								)
								: <HeaderLogo />
							}
						</HeaderMenuItem>
						{menuItems}
					</MenuItemsWrapper>
				</StyledLeftSideMenu>
			</>
		);
	}

	handleOverlayClick = () => {
		const { dispatch } = this.props;
		dispatch({ type: MenuActionKeys.REQUEST_CLOSE_LEFT_MENU });
	}

	handleAvatarClick = () => {
		RoutesUtils.historyPush(RoutesUtils.getPathProfile());
	}

	getMenuItems = (items: MenuItem[]) => {
		const {
			hasLogOutButton,
			hasProposalStatsPage,
			hasCookieSettings,
			hasNotesPage,
			hasDownloads,
			privateDirId,
			sharedDirId,
			archiveDirId,
			dispatch,
		} = this.props;

		if (hasDownloads) {
			const donwloadsItem = { type: MenuItemType.Downloads } as MenuItem;
			items.push(donwloadsItem);
		}

		if (hasProposalStatsPage) {
			const proposalStatsButton = { type: MenuItemType.ProposalStats } as MenuItem;
			items.push(proposalStatsButton);
		}

		if (hasNotesPage) {
			const notesButton = { type: MenuItemType.Notes } as MenuItem;
			items.push(notesButton);
		}

		if (hasCookieSettings) {
			const cookieSettingsItem = { type: MenuItemType.CookieSettings } as MenuItem;
			items.push(cookieSettingsItem);
		}

		if (hasLogOutButton) {
			const logOutButtonItem = { type: MenuItemType.LogOut } as MenuItem;
			items.push(logOutButtonItem);
		}

		return items.map(({ type, icon, label, target, options }, index) => {
			let itemArgs;

			switch (type) {
				case MenuItemType.Home:
					itemArgs = {
						type: MenuItemType.Home,
						label: label || 'messages.home',
						icon: icon || 'iconset_home',
						target: RoutesUtils.getPathHome()
					};
					break;
				case MenuItemType.Gallery:
					itemArgs = {
						type: MenuItemType.Gallery,
						label: label || 'messages.my_gallery',
						icon: icon || 'iconset_image',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, null)
					};
					break;
				case MenuItemType.New:
					itemArgs = {
						type: MenuItemType.New,
						label: label || 'messages.new',
						icon: icon || 'iconset_uprez_menu_news',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.New, null),
						badge: this.props.newItemsNb
					};
					break;
				case MenuItemType.Selection:
					itemArgs = {
						type: MenuItemType.Selection,
						label: label || 'messages.my_selection',
						icon: icon || 'iconset_uprez_menu_selection',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Selection, null)
					};
					break;
				case MenuItemType.Downloads:
					itemArgs = {
						type: MenuItemType.Downloads,
						label: label || 'messages.my_downloads',
						icon: icon || 'iconset_uprez_download_bold',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Downloads, null)
					};
					break;
				case MenuItemType.SharedWithMe:
					itemArgs = {
						type: MenuItemType.SharedWithMe,
						label: label || 'messages.shared_with_me',
						icon: icon || 'iconset_shared_with_me',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, String(sharedDirId))
					};
					break;
				case MenuItemType.Documents:
					itemArgs = {
						type: MenuItemType.Documents,
						label: label || 'messages.my_documents',
						icon: icon || 'iconset_uprez_menu_folder',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, String(privateDirId)),
					};
					break;
				case MenuItemType.Parameters:
					itemArgs = {
						type: MenuItemType.Parameters,
						label: label || 'messages.parameters',
						icon: icon || 'iconset_parameters',
						target: RoutesUtils.getPathParameters()
					};
					break;
				case MenuItemType.Profile:
					itemArgs = {
						type: MenuItemType.Profile,
						label: label || 'messages.profile',
						icon: icon || 'iconset_uprez_profile',
						target: RoutesUtils.getPathProfile(),
					};
					break;
				case MenuItemType.ProposalStats:
					itemArgs = {
						type: MenuItemType.ProposalStats,
						label: label || 'messages.my_sent',
						icon: icon || 'iconset_uprez_send',
						target: RoutesUtils.getPathProposalStats(),
					};
					break;
				case MenuItemType.ProposalArchive:
					itemArgs = {
						type: MenuItemType.ProposalArchive,
						label: label || 'messages.archive_noun',
						icon: icon || 'iconset_uprez_archive',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, String(archiveDirId)),
					};
					break;
				case MenuItemType.Notes:
					itemArgs = {
						type: MenuItemType.Notes,
						label: label || 'messages.my_notes',
						icon: icon || 'iconset_uprez_notes',
						target: RoutesUtils.getPathNotes(),
					};
					break;
				case MenuItemType.SearchButton:
					itemArgs = {
						type: MenuItemType.SearchButton,
						label: label || 'messages.search',
						icon: icon || 'iconset_uprez_menu_search',
						target: RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Search, null),
					};
					break;
				case MenuItemType.Link:
					itemArgs = {
						type: MenuItemType.Link,
						label: label || 'Link',
						icon: icon || 'iconset_link',
						target,
					};
					break;
				case MenuItemType.Asset:
					itemArgs = {
						type: MenuItemType.Asset,
						label: label || 'Asset',
						icon: icon || 'iconset_file',
						target,
					};
					break;
				case MenuItemType.Folder:
					itemArgs = {
						type: MenuItemType.Folder,
						label: label || 'Folder',
						icon: icon || 'iconset_folder',
						target,
					};
					break;
				case MenuItemType.Page:
					itemArgs = {
						type: MenuItemType.Page,
						label: label || 'Page',
						icon: icon || 'iconset_page',
						target,
					};
					break;
				case MenuItemType.CookieSettings:
						itemArgs = {
							type: MenuItemType.CookieSettings,
							label: label || 'messages.cookie_settings',
							icon: icon || 'iconset_cookie',
							target: '',
						};
						break;
				case MenuItemType.LogOut:
					itemArgs = {
						type: MenuItemType.LogOut,
						label: label || 'messages.log_out',
						icon: icon || 'iconset_logout',
						target: ''
					};
					break;
				case MenuItemType.CreateButton:
					return (
						<ButtonCreate
							key={MenuItemType.CreateButton}
							position={MenuPosition.burger}
							options={options as CreateButtonOption[]}
							onOptionClick={() => dispatch({ type: MenuActionKeys.REQUEST_CLOSE_LEFT_MENU })}
						/>
					);
				case MenuItemType.CreateNote:
					return (
						<ButtonCreateNote
							key={MenuItemType.CreateNote}
							position={MenuPosition.burger}
							label={label}
							icon={icon}
						/>
					);
				case MenuItemType.BackToPublication:
					return (
						<BackToPublication
							key={MenuItemType.BackToPublication}
							position={MenuPosition.burger}
							label={label}
							icon={icon}
						/>
					);
			}
			return this.renderMenuItem(itemArgs, index);
		});
	}
}

export default LeftSideMenu;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/MenuManager/Left/index.tsx